/**
 * Sortable rule row for List Tag Rules
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { GripVertical, Pencil, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@/components/ui';
import { Switch } from '@/components/ui';
import { Checkbox } from '@/components/ui';
import type { TagRuleScope } from '@/features/tag-rule-scope';
import type { ListTagRule } from '@/hooks';
import { cn } from '@/lib/utils';

import { formatCondition, getRuleDateLabel } from '../utils';
import { ScopedConditionLine } from './ScopedConditionLine';

interface SortableRuleRowProps {
  rule: ListTagRule;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
  isReorderPending: boolean;
  /** When provided, shows checkbox for selection */
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  /** Country/campaign in view; values render as that scope resolves them. */
  scope?: TagRuleScope;
}

export const SortableRuleRow: FC<SortableRuleRowProps> = ({
  rule,
  onEdit,
  onDelete,
  onToggleEnabled,
  isReorderPending,
  selected,
  onSelectChange,
  scope,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
    ref={setNodeRef}
    style={style}
    className={cn(
      'flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-muted/30',
      !rule.enabled && 'opacity-60',
      isDragging && 'z-10 opacity-90 shadow-lg',
    )}
  >
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {onSelectChange != null && (
        <Checkbox
          checked={selected ?? false}
          onCheckedChange={(c) => onSelectChange(c === true)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${rule.name}`}
          className="shrink-0"
        />
      )}
      <button
        type="button"
        className={cn(
          'flex touch-none shrink-0 cursor-grab items-center justify-center rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
          isDragging && 'cursor-grabbing',
          isReorderPending && 'pointer-events-none opacity-50',
        )}
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {rule.color && (
            <span
              className="h-4 w-4 shrink-0 rounded border border-border"
              style={{ backgroundColor: rule.color }}
              title={rule.color}
            />
          )}
          <span className="font-medium">
            {rule.emoji ? `${rule.emoji} ` : ''}
            {rule.name}
          </span>
          {rule.singleSourceOnly && (
            <span
              className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              title="Applies only to single-sourced lists (drop rules: drops sent to such a list)"
            >
              Single source only
            </span>
          )}
          {!!rule.listSystemTags?.length && (
            <span
              className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              title="Applies only to drops whose list has this system tag"
            >
              List: {rule.listSystemTags.join(" / ")}
            </span>
          )}
          {!rule.enabled && (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              Disabled
            </span>
          )}
        </div>
        <ScopedConditionLine
          conditions={rule.conditions}
          scope={scope}
          formatBase={formatCondition}
        />
        <p className="mt-0.5 text-xs text-muted-foreground">
          {getRuleDateLabel(rule)}
          {rule.minVolume !== undefined ? ` · from ${rule.minVolume.toLocaleString('en-GB')} unique visits an hour` : ''}
        </p>
      </div>
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <label className="flex cursor-pointer items-center gap-2" title={rule.enabled ? 'Disable' : 'Enable'}>
        <Switch
          checked={!!rule.enabled}
          onCheckedChange={onToggleEnabled}
          disabled={isReorderPending}
        />
        <span className="sr-only">{rule.enabled ? 'Disable rule' : 'Enable rule'}</span>
      </label>
      <Button
        variant="ghost"
        size="icon"
        onClick={onEdit}
        className="h-9 w-9"
        aria-label="Edit rule"
        title="Edit"
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={onDelete}
        disabled={isReorderPending}
        className="h-9 w-9 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        aria-label="Delete rule"
        title="Delete"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
    </div>
  );
};
